import { useEffect, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '@/i18n/intl';
import { digitsOnly } from './numericInput';

/**
 * Hold what the user is TYPING in a numeric field whose committed value is a
 * number.
 *
 * A number cannot represent a value mid-typing. "−50" passes through "−" on its
 * way in, and "−" is not a number, so a box rendered straight off the parent's
 * `null` loses the minus under the cursor and commits 50 — a value the user
 * never typed and is never told about. That silent rewrite is the exact failure
 * the `type="number"` ruling (grammar v3.3) exists to end, so replacing the
 * spin button without this buffer would have re-introduced it one layer down.
 *
 * The draft re-seeds only when the committed value moves for a reason that is
 * NOT our own keystroke round-tripping — a reset, a prefill, a server value
 * arriving — which is what keeps the cursor still while somebody types.
 *
 * The decimal separator is the reader's (#2109 R3-B6). `digitsOnly` accepts a
 * comma or a period and normalizes to a period; the box then SHOWS the
 * locale's separator ("12,50" in es/fr/pt, "12.50" in en), and a committed
 * value seeds with it. The committed number is locale-free.
 */
export function useNumericDraft(
  committed: number | null | undefined,
  onCommit: (next: number | null) => void,
  { decimal = false }: { decimal?: boolean } = {},
) {
  // Subscribe to language changes so the separator follows a switch.
  useTranslation();
  const sep = decimal ? decimalSeparator() : '.';
  const show = (normalized: string) => (sep === '.' ? normalized : normalized.replace('.', sep));
  const asText = committed == null ? '' : show(String(committed));
  const [draft, setDraft] = useState(asText);

  useEffect(() => {
    setDraft((prev) => (toNumber(prev) === (committed ?? null) ? prev : asText));
    // `asText` is derived from `committed`; tracking the value is the intent.
  }, [committed, asText]);

  return {
    value: draft,
    onChange: (e: ChangeEvent<HTMLInputElement>) => {
      const next = digitsOnly(e.target.value, { decimal });
      setDraft(show(next));
      onCommit(toNumber(next));
    },
  };
}

/** `''`, `'-'` and `'-.'` are all "not a number yet", not zero. Either separator reads as the decimal point. */
function toNumber(raw: string): number | null {
  const normalized = raw.replace(',', '.');
  if (normalized === '' || normalized === '-' || normalized === '.' || normalized === '-.') return null;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

/** The active locale's decimal separator: "." in en, "," in es/pt/fr. */
function decimalSeparator(): string {
  return formatNumber(1.5).replace(/\d/g, '') || '.';
}
